import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { AuraBaseDesignTokens } from '@primeng/themes/aura/base';
import { PaletteDesignToken, Preset } from '@primeng/themes/types';

/**
 * The app's PrimeNG preset — currently the "Signal Light" brand theme. The filename names the
 * file's role, not the brand generation, so the next rebrand is not a rename.
 *
 * Theme-package imports are confined to this file, and must come from `@primeng/themes`:
 * `@primeuix/themes` is not installable against PrimeNG 19 and would resolve a second
 * `@primeuix/styled` copy, silently leaving the app unthemed.
 *
 * **Brand colour is declared exactly once, in `primitive`.** No block below it may carry a bare
 * hex, and `styles.scss` reaches brand colour by reference through `@theme`, never by value.
 */

/**
 * Extending the typed tree rather than reaching for `extend` keeps every token path
 * compile-checked, so a typo fails the build instead of silently rendering nothing.
 */
interface SignalLightDesignTokens extends AuraBaseDesignTokens {
  primitive?: NonNullable<AuraBaseDesignTokens['primitive']> & {
    /** The brand's second radius value; Aura has no equivalent. */
    borderRadius?: { pill?: string };
    cobalt?: PaletteDesignToken & { tint?: string };
    signal?: { 500?: string; 600?: string; 700?: string };
    sand?: PaletteDesignToken & {
      0?: string;
      veil?: string;
      mist?: string;
      raised?: string;
      soft?: string;
      track?: string;
      striped?: string;
    };
    success?: StatusPrimitive;
    warn?: StatusPrimitive;
    danger?: StatusPrimitive;
    info?: StatusPrimitive;
    idle?: { 500?: string };
    elevation?: { overlay?: string; modal?: string; focus?: string };
    control?: { height?: string };
  };
}

/** A semantic status colour and its background/foreground pair; `600` is the hover step. */
interface StatusPrimitive {
  500?: string;
  600?: string;
  bg?: string;
  fg?: string;
}

const preset: Preset<SignalLightDesignTokens> = {
  primitive: {
    /*
     * One of exactly two blocks radius flows through — the other is `--radius-*` in
     * `styles.scss` — so a flip to 0px stays a two-block edit. `pill` is excluded from any such
     * flip, or chips, toggles and slider handles lose their round shape.
     */
    borderRadius: {
      none: '0',
      // Tiny controls only: on a 13px checkbox square, 4px reads as a circle.
      xs: '2px',
      // The brand radius is a flat 4px, so every step a surface can reach is 4px. The export's
      // 6px/8px `lg`/`xl` are unreferenced and would break the two-blocks-one-unit premise.
      sm: '4px',
      md: '4px',
      lg: '4px',
      xl: '4px',
      pill: '999px',
    },

    /* ---- Brand palettes: the single source of brand colour ---- */

    /** Primary. `tint` is the selected-row / highlight surface. */
    cobalt: {
      50: '#EAEDFF',
      100: '#CBD3FF',
      200: '#9FADFF',
      300: '#6E86FF',
      400: '#4C68FF',
      500: '#2F4BFF',
      600: '#1B32C4',
      700: '#233C84',
      800: '#1B2A55',
      900: '#161F3F',
      950: '#0E1428',
      tint: '#F7F8FF',
    },

    /** A **highlight only**: never a chart series, never text, never a surface fill. */
    signal: {
      500: '#F5E663',
      600: '#C79A00',
      700: '#6A5A00',
    },

    /** `100` is the app background, `0` cards and tables, `900` obsidian. */
    sand: {
      0: '#FFFFFF',
      50: '#FAF9F5',
      100: '#F2F0EA',
      200: '#E4E0D6',
      300: '#D8D3C7',
      400: '#A8A498',
      500: '#8A8F87',
      600: '#5C6270',
      700: '#4C5157',
      800: '#2A2E36',
      900: '#0E1116',
      950: '#080A0E',
      veil: 'rgba(242, 240, 234, 0.16)',
      /** Off-scale, for the obsidian nav: the sand steps are too warm to read on a dark surface. */
      mist: '#9BA3B2',
      raised: '#171C27',
      /**
       * For lines and fills *inside* a surface rather than around it. The numbered scale steps
       * straight from `50` to `200`, so without these a row separator draws at the weight of the
       * table's own frame and a stripe as dark as a hover.
       */
      soft: '#F2EFE8',
      track: '#EAE7DE',
      striped: '#FCFBF8',
    },

    /*
     * `fg` is the only value the brand pairs with text, and the only one clearing AA on both
     * white and its own `bg`. `styles.scss` projects Tailwind's `red`/`amber` onto these.
     */
    success: { 500: '#0FA37C', 600: '#0A6B52', bg: '#DFF7EE', fg: '#0A6B52' },
    warn: { 500: '#C79A00', 600: '#9C7800', bg: '#FFF6D6', fg: '#6A5A00' },
    danger: { 500: '#E02465', 600: '#B3123F', bg: '#FFE3EC', fg: '#A50E45' },
    /** Info is cobalt — by reference, so the value is still written once. */
    info: {
      500: '{cobalt.500}',
      600: '{cobalt.600}',
      bg: '{cobalt.50}',
      fg: '{cobalt.600}',
    },
    idle: { 500: '#6B7689' },

    /** Overlays only: in-plane surfaces carry a 1px border and no shadow. */
    elevation: {
      overlay: '0 8px 24px rgba(14, 17, 22, 0.10)',
      modal: '0 24px 64px rgba(14, 17, 22, 0.18)',
      focus: '0 0 0 3px rgba(47, 75, 255, 0.16)',
    },

    /**
     * Every control lands here by construction: 8px of padding above and below a line pinned to
     * 1.2. An icon-only button takes it as its width too, so it reads as a square, not a pill.
     */
    control: { height: '34px' },
  },

  semantic: {
    primary: {
      50: '{cobalt.50}',
      100: '{cobalt.100}',
      200: '{cobalt.200}',
      300: '{cobalt.300}',
      400: '{cobalt.400}',
      500: '{cobalt.500}',
      600: '{cobalt.600}',
      700: '{cobalt.700}',
      800: '{cobalt.800}',
      900: '{cobalt.900}',
      950: '{cobalt.950}',
    },

    /**
     * The focus treatment, declared once: a 1px cobalt border plus a 3px cobalt-tinted ring.
     * No second global `:focus-visible` rule may exist — an unqualified `box-shadow`
     * there would stomp the overlay elevation.
     *
     * On a *filled* cobalt control the 1px border matches the fill, so the ring alone carries
     * the affordance and lands near 1.3:1 against the unfocused state. Bordered controls are
     * unaffected. Changing that is a change to this block and nowhere else.
     */
    focusRing: {
      width: '1px',
      style: 'solid',
      color: '{cobalt.500}',
      offset: '0',
      shadow: '{elevation.focus}',
    },

    formField: {
      paddingX: '11px',
      paddingY: '8px',
      borderRadius: '{border.radius.sm}',
      focusRing: {
        width: '1px',
        style: 'solid',
        color: '{cobalt.500}',
        offset: '0',
        shadow: '{elevation.focus}',
      },
    },

    content: { borderRadius: '{border.radius.sm}' },

    overlay: {
      select: {
        borderRadius: '{border.radius.sm}',
        shadow: '{elevation.overlay}',
      },
      popover: {
        borderRadius: '{border.radius.sm}',
        shadow: '{elevation.overlay}',
      },
      modal: {
        borderRadius: '{border.radius.sm}',
        shadow: '{elevation.modal}',
        padding: '24px',
      },
    },

    colorScheme: {
      /*
       * Light only. Signal Light specifies no dark palette, and `darkModeSelector` in
       * `app.config.ts` is bound to a never-applied class so neither dark trigger fires
       * — authoring a half-finished `dark` block here would produce a third
       * colour scheme nobody designed.
       */
      light: {
        surface: {
          0: '{sand.0}',
          50: '{sand.50}',
          100: '{sand.100}',
          200: '{sand.200}',
          300: '{sand.300}',
          400: '{sand.400}',
          500: '{sand.500}',
          600: '{sand.600}',
          700: '{sand.700}',
          800: '{sand.800}',
          900: '{sand.900}',
          950: '{sand.950}',
        },
        primary: {
          color: '{cobalt.500}',
          contrastColor: '{sand.100}',
          hoverColor: '{cobalt.600}',
          activeColor: '{cobalt.700}',
        },
        highlight: {
          background: '{cobalt.tint}',
          focusBackground: '{cobalt.50}',
          color: '{cobalt.600}',
          focusColor: '{cobalt.700}',
        },
        text: {
          color: '{sand.900}',
          hoverColor: '{sand.900}',
          mutedColor: '{sand.600}',
          hoverMutedColor: '{sand.700}',
        },
        content: {
          background: '{sand.0}',
          hoverBackground: '{sand.50}',
          borderColor: '{sand.200}',
          color: '{sand.900}',
        },
        formField: {
          background: '{sand.0}',
          disabledBackground: '{sand.50}',
          filledBackground: '{sand.50}',
          borderColor: '{sand.300}',
          hoverBorderColor: '{sand.400}',
          focusBorderColor: '{cobalt.500}',
          invalidBorderColor: '{danger.500}',
          color: '{sand.900}',
          disabledColor: '{sand.400}',
          placeholderColor: '{sand.500}',
          floatLabelColor: '{sand.500}',
        },
      },
    },
  },

  components: {
    button: {
      root: {
        borderRadius: '{border.radius.sm}',
        paddingX: '18px',
        // 8px, matching `formField.paddingY`, so a button and the input beside it are the same
        // height. The export's 9px made every button 2px taller than its neighbours.
        paddingY: '8px',
        gap: '8px',
        label: { fontWeight: '600' },
        iconOnlyWidth: '{control.height}',
        sm: { paddingX: '13px', paddingY: '6px' },
        lg: { paddingX: '22px', paddingY: '12px' },
      },
      colorScheme: {
        light: {
          root: {
            primary: {
              background: '{cobalt.500}',
              hoverBackground: '{cobalt.600}',
              activeBackground: '{cobalt.700}',
              borderColor: '{cobalt.500}',
              hoverBorderColor: '{cobalt.600}',
              activeBorderColor: '{cobalt.700}',
              color: '{sand.100}',
            },
            secondary: {
              background: '{sand.0}',
              hoverBackground: '{sand.50}',
              borderColor: '{sand.300}',
              hoverBorderColor: '{sand.400}',
              color: '{sand.900}',
            },
            // The export set signal yellow as this label colour, which the brand forbids outright.
            // Contrast severity is what the sidebar toggle and logout use, so it is visible on
            // every screen — it takes the inverse-sand text value instead.
            contrast: {
              background: '{sand.900}',
              hoverBackground: '{sand.800}',
              borderColor: '{sand.900}',
              color: '{sand.100}',
            },
            danger: {
              background: '{danger.500}',
              hoverBackground: '{danger.600}',
              borderColor: '{danger.500}',
              color: '{sand.0}',
            },
            success: {
              background: '{success.500}',
              hoverBackground: '{success.600}',
              borderColor: '{success.500}',
              color: '{sand.0}',
            },
            warn: {
              background: '{warn.500}',
              hoverBackground: '{warn.600}',
              borderColor: '{warn.500}',
              color: '{sand.0}',
            },
            // Info is cobalt. Without this it stays on Aura's stock sky palette — an
            // off-brand blue that the installed user-management package renders today.
            info: {
              background: '{info.500}',
              hoverBackground: '{info.600}',
              borderColor: '{info.500}',
              color: '{sand.100}',
            },
          },
          /*
           * The outlined and text variants need their own colours: Aura points every one of
           * them at a palette step chosen for *its* scale, so left alone the app's standard
           * neutral button — `severity="secondary" [outlined]` per AGENTS.md — takes
           * `surface.500`, which is ~3.3:1 on white and fails AA for a button label. Each
           * label below is a foreground value, and the semantic ones reuse the same `fg` steps
           * the notice surfaces use, so a bordered and a filled control of the same severity
           * read as the same thing.
           */
          outlined: {
            primary: {
              borderColor: '{cobalt.500}',
              color: '{cobalt.500}',
              hoverBackground: '{cobalt.50}',
              activeBackground: '{cobalt.100}',
            },
            secondary: {
              borderColor: '{sand.300}',
              color: '{sand.700}',
              hoverBackground: '{sand.50}',
              activeBackground: '{sand.100}',
            },
            info: {
              borderColor: '{info.500}',
              color: '{info.fg}',
              hoverBackground: '{info.bg}',
              activeBackground: '{cobalt.100}',
            },
            success: {
              borderColor: '{success.500}',
              color: '{success.fg}',
              hoverBackground: '{success.bg}',
              activeBackground: '{success.bg}',
            },
            warn: {
              borderColor: '{warn.500}',
              color: '{warn.fg}',
              hoverBackground: '{warn.bg}',
              activeBackground: '{warn.bg}',
            },
            danger: {
              borderColor: '{danger.500}',
              color: '{danger.fg}',
              hoverBackground: '{danger.bg}',
              activeBackground: '{danger.bg}',
            },
            contrast: {
              borderColor: '{sand.900}',
              color: '{sand.900}',
              hoverBackground: '{sand.100}',
              activeBackground: '{sand.200}',
            },
          },
          text: {
            primary: { color: '{cobalt.500}', hoverBackground: '{cobalt.50}' },
            secondary: {
              color: '{sand.700}',
              hoverBackground: '{sand.50}',
              activeBackground: '{sand.100}',
            },
            info: { color: '{info.fg}', hoverBackground: '{info.bg}' },
            success: { color: '{success.fg}', hoverBackground: '{success.bg}' },
            warn: { color: '{warn.fg}', hoverBackground: '{warn.bg}' },
            danger: { color: '{danger.fg}', hoverBackground: '{danger.bg}' },
            // The two contrast text buttons both sit on the cobalt topbar, so they take the
            // inverse-sand label and a translucent sand hover rather than the retired
            // accent-green palette (TR8).
            contrast: {
              color: '{sand.100}',
              hoverBackground: '{sand.veil}',
              activeBackground: '{sand.veil}',
            },
          },
        },
      },
    },

    card: {
      root: {
        background: '{sand.0}',
        borderRadius: '{border.radius.sm}',
        color: '{sand.900}',
        // In-plane surfaces carry a 1px border and no shadow.
        shadow: 'none',
      },
      body: { padding: '18px', gap: '12px' },
      caption: { gap: '4px' },
      title: { fontSize: '15px', fontWeight: '600' },
      // sand.500 computes to roughly 3.3:1 on white and fails AA for normal text; the muted
      // body value is the floor for a text role, and sand.500 is reserved for icons and
      // disabled states where the 3:1 non-text threshold applies.
      subtitle: { color: '{sand.600}' },
      // The card has a radius token and a shadow token but no border token, and in-plane surfaces want the
      // hairline that replaces the shadow.
      css: () => `
        .p-card {
            border: 1px solid var(--p-content-border-color);
        }
        `,
    },

    datatable: {
      header: {
        background: '{sand.0}',
        borderColor: '{sand.200}',
        padding: '16px 18px',
      },
      headerCell: {
        background: '{sand.50}',
        hoverBackground: '{sand.100}',
        selectedBackground: '{cobalt.50}',
        borderColor: '{sand.track}',
        color: '{sand.600}',
        padding: '10px 18px',
        gap: '6px',
      },
      // A 44px row: 11px above and below a 13.5px/1.55 line of text. The export's 13px
      // was measured against a different body size and lands near 47px here.
      // The separator is the soft hairline, not the frame colour — `sand.200` here drew every
      // row division at the same weight as the table's outer border.
      bodyCell: { borderColor: '{sand.soft}', padding: '11px 18px' },
      row: {
        background: '{sand.0}',
        hoverBackground: '{sand.50}',
        selectedBackground: '{cobalt.tint}',
        color: '{sand.900}',
      },
      footer: { background: '{sand.0}', borderColor: '{sand.200}' },
      paginatorTop: { borderColor: '{sand.200}' },
      paginatorBottom: { borderColor: '{sand.200}' },
      sortIcon: { color: '{sand.500}', hoverColor: '{cobalt.500}' },
      // Aura declares exactly these three leaves inside its own light scheme, which outranks a
      // bare-level override — so they have to be set here or they do not apply at all.
      colorScheme: {
        light: {
          root: { borderColor: '{sand.200}' },
          row: { stripedBackground: '{sand.striped}' },
          bodyCell: { selectedBorderColor: '{cobalt.200}' },
        },
      },
      /*
       * Two rules no datatable token can express: the outer hairline every surface carries
       * (Aura draws one only with `showGridlines`), and the brand's header treatment — a mono
       * micro-label rather than bold body text. Row height and body size are *not* here: both
       * are reachable from tokens (see `bodyCell.padding` above and the body rule in
       * `styles.scss`), and the same intent must not live in two homes.
       */
      css: () => `
        .p-datatable {
            border: 1px solid var(--p-datatable-border-color);
        }
        .p-datatable .p-datatable-thead > tr > th {
            font-family: var(--font-mono);
            font-size: var(--text-label);
            letter-spacing: var(--tracking-label);
            text-transform: uppercase;
            font-weight: 400;
        }
        `,
    },

    paginator: {
      root: {
        background: '{sand.0}',
        padding: '12px 18px',
        gap: '3px',
        borderRadius: '{border.radius.none}',
      },
      navButton: {
        background: 'transparent',
        hoverBackground: '{sand.100}',
        selectedBackground: '{sand.900}',
        color: '{sand.700}',
        selectedColor: '{sand.100}',
        width: '30px',
        height: '30px',
        borderRadius: '{border.radius.sm}',
      },
      // Page numbers are pure numerals, so they take the mono face with tabular figures.
      // There is no font token for them, and the app's two paginated tables are the only
      // markup-free consumer this clause has.
      css: () => `
        .p-paginator {
            font-family: var(--font-mono);
            font-variant-numeric: tabular-nums;
        }
        `,
    },

    select: {
      root: {
        borderRadius: '{border.radius.sm}',
        paddingX: '11px',
        paddingY: '8px',
      },
      overlay: {
        borderRadius: '{border.radius.sm}',
        shadow: '{elevation.overlay}',
      },
      option: {
        padding: '7px 11px',
        borderRadius: '{border.radius.none}',
        selectedBackground: '{cobalt.tint}',
        selectedColor: '{sand.900}',
        focusBackground: '{sand.50}',
      },
      optionGroup: {
        background: '{sand.50}',
        color: '{sand.600}',
        fontWeight: '500',
      },
    },

    multiselect: {
      root: {
        borderRadius: '{border.radius.sm}',
        paddingX: '10px',
        paddingY: '7px',
      },
      chip: { borderRadius: '{border.radius.sm}' },
      option: { padding: '7px 11px', selectedBackground: '{cobalt.tint}' },
      overlay: { shadow: '{elevation.overlay}' },
    },

    chip: {
      root: {
        borderRadius: '{border.radius.pill}',
        paddingX: '12px',
        paddingY: '6px',
      },
      colorScheme: {
        light: {
          root: { background: '{cobalt.50}', color: '{cobalt.500}' },
          icon: { color: '{cobalt.500}' },
        },
      },
      css: () => `
        .p-chip-remove-icon {
            line-height: normal;
        }
        `,
    },

    tag: {
      root: {
        borderRadius: '{border.radius.none}',
        fontSize: '10.5px',
        fontWeight: '400',
        padding: '4px 9px',
      },
      colorScheme: {
        light: {
          primary: { background: '{cobalt.50}', color: '{cobalt.600}' },
          success: { background: '{success.bg}', color: '{success.fg}' },
          warn: { background: '{warn.bg}', color: '{warn.fg}' },
          danger: { background: '{danger.bg}', color: '{danger.fg}' },
          secondary: { background: '{sand.100}', color: '{sand.600}' },
          // Signal yellow as tag *text* is forbidden; inverse sand instead.
          contrast: { background: '{sand.900}', color: '{sand.100}' },
        },
      },
    },

    selectbutton: {
      root: { borderRadius: '{border.radius.sm}' },
    },

    /*
     * The segmented control. Its whole palette lives in `colorScheme.light` because Aura
     * declares it there too, and a scheme block outranks a bare-level override — written at
     * root level these values are emitted but never applied, which left the selected segment
     * rendering Aura's white-chip-on-grey instead of the brand's obsidian fill.
     */
    togglebutton: {
      root: {
        borderRadius: '{border.radius.sm}',
        padding: '3px',
        fontWeight: '500',
      },
      content: { padding: '5px 14px', checkedShadow: 'none' },
      colorScheme: {
        light: {
          root: {
            background: '{sand.100}',
            checkedBackground: '{sand.100}',
            hoverBackground: '{sand.100}',
            borderColor: '{sand.300}',
            color: '{sand.700}',
            hoverColor: '{sand.900}',
            checkedColor: '{sand.100}',
            checkedBorderColor: '{sand.300}',
          },
          // The selected segment is the obsidian chip, so its label takes the inverse-sand
          // value above rather than the darkest text colour.
          content: { checkedBackground: '{sand.900}' },
          icon: {
            color: '{sand.600}',
            hoverColor: '{sand.900}',
            checkedColor: '{sand.100}',
          },
        },
      },
    },

    toggleswitch: {
      root: {
        width: '30px',
        height: '17px',
        borderRadius: '{border.radius.pill}',
        gap: '2px',
      },
      handle: { size: '13px', borderRadius: '{border.radius.pill}' },
      colorScheme: {
        light: {
          root: {
            background: '{sand.300}',
            hoverBackground: '{sand.400}',
            checkedBackground: '{cobalt.500}',
            checkedHoverBackground: '{cobalt.600}',
          },
          handle: {
            background: '{sand.0}',
            checkedBackground: '{sand.0}',
          },
        },
      },
    },

    checkbox: {
      root: {
        width: '13px',
        height: '13px',
        borderRadius: '{border.radius.xs}',
        borderColor: '{sand.300}',
        checkedBackground: '{cobalt.500}',
        checkedBorderColor: '{cobalt.500}',
        checkedHoverBackground: '{cobalt.600}',
      },
      icon: { size: '9px', checkedColor: '{sand.0}' },
    },

    radiobutton: {
      root: {
        width: '15px',
        height: '15px',
        borderColor: '{sand.300}',
        checkedBackground: '{sand.0}',
        checkedBorderColor: '{cobalt.500}',
      },
      icon: { size: '7px', checkedColor: '{cobalt.500}' },
    },

    slider: {
      track: { background: '{sand.track}', size: '4px' },
      range: { background: '{cobalt.500}' },
      // The export asked for a white handle with a cobalt border, which is not how the
      // component is built: PrimeNG paints the handle as an outer disc plus an inner content
      // disc, so the ring is the handle background and the white is the content.
      handle: {
        width: '14px',
        height: '14px',
        borderRadius: '{border.radius.pill}',
        background: '{cobalt.500}',
        content: {
          width: '10px',
          height: '10px',
          borderRadius: '{border.radius.pill}',
          shadow: 'none',
        },
      },
      // Same scheme-block rule: Aura owns the handle's inner disc colour in its light scheme.
      colorScheme: {
        light: { handle: { content: { background: '{sand.0}' } } },
      },
    },

    tabs: {
      tablist: { background: 'transparent', borderColor: '{sand.200}' },
      tab: {
        background: 'transparent',
        color: '{sand.600}',
        activeColor: '{sand.900}',
        hoverColor: '{sand.700}',
        padding: '8px 14px',
        fontWeight: '500',
        activeBorderColor: '{cobalt.500}',
        borderWidth: '0 0 2px 0',
      },
      /*
       * 24px above the panel, on the 8pt grid, rather than the export's 16px. A panel now opens
       * onto a 10px uppercase caption whose line box is 4px shorter than the 12px body caption
       * it replaced, and whose caps sit flush to the top of that box — so the same padding read
       * as crowding the tab strip's active bar directly above it. No side padding, so panel
       * content stays aligned with the tab labels.
       */
      tabpanel: { background: 'transparent', padding: '24px 0 16px' },
      activeBar: { height: '2px', background: '{cobalt.500}' },
      /*
       * Without this the padding above is emitted and then silently discarded. PrimeNG puts the
       * `p-tabpanels` / `p-tabpanel` classes on the Angular host elements, and a custom element
       * with no `display` declared is `inline` — where vertical padding paints but does not
       * affect layout, so the panel's content still sat flush against the tab strip's active bar.
       * The template already carries a hand-added `block` on `<p-tabs>` for the same reason; this
       * covers the two levels below it, and covers any future tabs instance rather than one call
       * site.
       */
      css: () => `
        .p-tabpanels,
        .p-tabpanel {
            display: block;
        }
        `,
    },

    menu: {
      root: {
        background: '{sand.0}',
        borderColor: '{sand.200}',
        borderRadius: '{border.radius.sm}',
        shadow: '{elevation.overlay}',
      },
      item: {
        padding: '8px 12px',
        borderRadius: '{border.radius.none}',
        focusBackground: '{sand.50}',
        color: '{sand.900}',
      },
      submenuLabel: {
        background: 'transparent',
        color: '{sand.600}',
        fontWeight: '500',
        padding: '8px 12px',
      },
    },

    tooltip: {
      root: {
        borderRadius: '{border.radius.sm}',
        padding: '10px 12px',
        maxWidth: '260px',
        shadow: '{elevation.modal}',
      },
      // Obsidian, not Aura's slate: same scheme-block rule as the segmented control above.
      colorScheme: {
        light: { root: { background: '{sand.900}', color: '{sand.100}' } },
      },
    },

    message: {
      root: { borderRadius: '{border.radius.sm}', borderWidth: '0 0 0 3px' },
      colorScheme: {
        light: {
          info: {
            background: '{info.bg}',
            borderColor: '{info.500}',
            color: '{info.fg}',
            shadow: 'none',
          },
          success: {
            background: '{success.bg}',
            borderColor: '{success.500}',
            color: '{success.fg}',
            shadow: 'none',
          },
          warn: {
            background: '{warn.bg}',
            borderColor: '{warn.500}',
            color: '{warn.fg}',
            shadow: 'none',
          },
          error: {
            background: '{danger.bg}',
            borderColor: '{danger.500}',
            color: '{danger.fg}',
            shadow: 'none',
          },
        },
      },
    },

    progressbar: {
      root: {
        background: '{sand.track}',
        borderRadius: '{border.radius.none}',
        height: '6px',
      },
      value: { background: '{cobalt.500}' },
    },

    dialog: {
      root: {
        borderRadius: '{border.radius.sm}',
        shadow: '{elevation.modal}',
      },
      header: { padding: '20px 24px' },
      content: { padding: '0 24px 24px' },
    },

    inputtext: {
      root: {
        borderRadius: '{border.radius.sm}',
        paddingX: '11px',
        paddingY: '8px',
      },
    },

    datepicker: {
      // The datepicker has no root radius token; the panel is the surface that carries it.
      panel: {
        borderRadius: '{border.radius.sm}',
        shadow: '{elevation.overlay}',
      },
      date: {
        borderRadius: '{border.radius.xs}',
        selectedBackground: '{cobalt.500}',
        selectedColor: '{sand.100}',
        rangeSelectedBackground: '{cobalt.50}',
        rangeSelectedColor: '{cobalt.600}',
      },
      // Same scheme-block rule: Aura owns `today` in its light scheme.
      colorScheme: {
        light: { today: { background: '{warn.bg}', color: '{warn.fg}' } },
      },
    },

    /*
     * The five blocks below cover the app shell, which the brand export does not touch (TR9).
     * Without them the topbar, sidebar and breadcrumb revert to Aura defaults.
     */

    menubar: {
      root: {
        // The topbar is a full-bleed cobalt bar with square corners, not a rounded surface.
        borderRadius: '{border.radius.none}',
      },
      colorScheme: {
        light: {
          /*
           * Obsidian, not cobalt. The shell's chrome is one dark surface: the topbar continues
           * straight into the sidebar below it, which is how the kit frames the app. A cobalt
           * bar above an obsidian nav read as two unrelated headers, and it also spent the
           * action colour on furniture — cobalt now means "this is actionable" everywhere.
           */
          root: { background: '{sand.900}', borderColor: 'transparent' },
          item: { color: '{sand.100}', focusBackground: 'transparent' },
        },
      },
      // App-wide menubar link styling, relocated here from app.component.scss.
      // Applies to every menubar, including the one the out-of-scope libs/shared/ui
      // sidebar-layout renders, without editing that lib.
      css: () => `
        .p-menubar-item-link {
            width: fit-content;
            font-weight: 600;
        }
        .p-menubar-item-link:focus-visible {
            /* Translucent sand: the bar is obsidian, so a solid sand block would flash bright. */
            background-color: var(--p-sand-veil);
        }
        .p-menubar-item-link.active {
            border-top: 3px solid transparent;
            border-bottom: 3px solid var(--p-signal-500);
        }
        `,
    },

    /*
     * The nav, which the app renders as the obsidian sidebar. Every colour here is a panelmenu
     * token rather than a hook rule, because Aura declares none of them inside a colour scheme;
     * only the panel surface itself and the two treatments no token reaches — the group label
     * and the active marker — are in the hook below.
     *
     * Text sits on obsidian, so the roles invert: `mist` carries a resting item, inverse sand
     * carries hover and the active item, and the raised obsidian is the hover surface.
     */
    panelmenu: {
      root: { gap: '0' },
      panel: {
        first: { borderWidth: '0' },
        last: { borderWidth: '0' },
        borderWidth: '0',
        padding: '0 0.25rem',
        // The `.layout-sidebar` hook paints the surface, so each panel is a pass-through.
        background: 'transparent',
        borderColor: 'transparent',
        color: '{sand.100}',
      },
      item: {
        padding: '0.5rem 0.75rem',
        color: '{sand.mist}',
        focusColor: '{sand.100}',
        focusBackground: '{sand.raised}',
        icon: { color: '{sand.mist}', focusColor: '{sand.100}' },
      },
      submenu: { indent: '1rem' },
      submenuIcon: { color: '{sand.mist}', focusColor: '{sand.100}' },
      css: () =>
        `
        .layout-sidebar {
            background: var(--p-sand-900);
            border-right: 1px solid var(--p-sand-800);
        }
        .p-panelmenu-header-link {
            display: flex;
            flex-direction: row;
        }
        .p-panelmenu-header-link > chevrondownicon,
        .p-panelmenu-header-link > chevronrighticon {
            order: 99;
            margin-left: auto;
            margin-right: 0;
        }
        /*
         * A group heading takes the brand's micro-label treatment, matching the kit's nav
         * eyebrows. Scoped by :has() to headings that actually own a submenu, so a top-level
         * destination such as "Start" stays a nav item and is not shrunk into a label. Only the
         * label span is restyled, so the item's icon keeps its size.
         *
         * The chevron *elements* are the discriminator, not the submenu-icon class: PrimeNG
         * puts that class on an item's own leading icon too, so keying on it labelled every
         * heading that merely has an icon.
         */
        .layout-sidebar .p-panelmenu-header-link:has(chevrondownicon, chevronrighticon)
            .p-panelmenu-header-label {
            font-family: var(--font-mono);
            font-size: var(--text-label);
            letter-spacing: var(--tracking-label);
            text-transform: uppercase;
            color: var(--p-sand-500);
        }
        /*
         * The current destination carries a cobalt marker, the way the kit marks its active nav
         * row. An inset box-shadow, not a border, so the label does not shift by 3px when the
         * route changes — and not elevation, so the no-shadow rule is untouched.
         */
        .layout-sidebar .p-panelmenu-item-link-active {
            background: var(--p-sand-raised);
            color: var(--p-sand-100);
            box-shadow: inset 3px 0 0 var(--p-cobalt-500);
        }
        .layout-sidebar .p-panelmenu-item-link-active .p-panelmenu-item-icon {
            color: var(--p-sand-100);
        }
        `,
    },

    toast: {
      root: { borderRadius: '{border.radius.sm}' },
      // The summary carries the outcome, so it takes the same weight as a button label.
      summary: { fontWeight: '600' },
    },

    breadcrumb: {
      root: { padding: '0.375rem 0', background: 'transparent' },
      /*
       * The trail reads as the kit's eyebrow above the page title: a micro-label rather than
       * body text. Applied to the list items so it reaches the custom `#item` template the app
       * supplies, which the component's own label class never wraps. FontAwesome declares its
       * own family, so the home icon is unaffected.
       */
      css: () => `
        .p-breadcrumb-list {
            font-family: var(--font-mono);
            font-size: var(--text-label);
            letter-spacing: var(--tracking-label);
            text-transform: uppercase;
        }
        `,
    },

    autocomplete: {
      chip: { borderRadius: '{border.radius.pill}' },
      overlay: { shadow: '{elevation.overlay}' },
    },
  },
};

/*
 * Control density, the one rule that has to span components.
 *
 * PrimeNG hardcodes `font-size: 1rem` on `.p-button` and `.p-inputtext` instead of exposing a
 * token, so until now *no* control took the brand's 13.5px base at all — every button, input and
 * select rendered 16px text, and with the body's 1.55 line-height inherited they stood ~43px
 * tall, a button 45px. Two visible symptoms: controls were far above the kit's declared 34px,
 * and a button was 2px taller than the input beside it because its padding was 1px more per side.
 *
 * Pinning the size and a tighter control line-height fixes both, and there is no token for
 * either. It lives at preset level rather than in one component's `css()` hook because it is one
 * intent across six surfaces — splitting it six ways is how the halves drift apart.
 *
 * Each class is **doubled deliberately**. PrimeNG's own `font-size: 1rem` is a single-class
 * selector, and this block is injected with the shared styles — before any component's own
 * stylesheet — so at equal specificity the component would win on source order. Doubling raises
 * specificity instead of relying on injection order, which is not ours to control. `.p-button-sm`
 * keeps its own smaller size for the same reason.
 */
preset.css = () => `
  .p-button.p-button,
  .p-inputtext.p-inputtext,
  .p-textarea.p-textarea,
  .p-select-label.p-select-label,
  .p-multiselect-label.p-multiselect-label,
  .p-togglebutton.p-togglebutton {
      font-size: var(--text-base);
      line-height: 1.2;
  }
  .p-button-sm.p-button-sm,
  .p-button-sm .p-button-icon {
      font-size: var(--text-sm);
  }
`;

export const CustomPreset = definePreset(Aura, preset);

/**
 * The class both dark-mode triggers are bound to — and which nothing ever applies.
 *
 * PrimeNG's `darkModeSelector` and Tailwind's `dark:` variant are independent, and both default
 * to `prefers-color-scheme`. Signal Light specifies light mode only, so leaving either on
 * `prefers-color-scheme` would hand every dark-OS user a scheme nobody designed: the shell
 * wrapper painting cobalt-dark while every PrimeNG surface stays warm sand.
 *
 * Turning dark mode back on is therefore adding this class to the document plus authoring a
 * `colorScheme.dark` block above. `styles.scss` binds Tailwind's variant to the same class by
 * literal — a stylesheet cannot import this constant — so the two must be changed together.
 */
export const DARK_MODE_SELECTOR = '.aiville-dark';
